import type { ICoord2D } from '@nova/types';

import { tileIndexAt } from './map-gen/tile-grid';

/**
 * Visits every grid tile a world-space segment crosses, in order from
 * `from` to `to`. Out-of-bounds cells are skipped; in-bounds tiles are visited once each.
 */
export function forEachTileIndexAlongWorldSegment(
  from: ICoord2D,
  to: ICoord2D,
  tileSize: number,
  widthTiles: number,
  heightTiles: number,
  visit: (tileIndex: number) => void
): void {
  if (tileSize <= 0 || widthTiles <= 0 || heightTiles <= 0) {
    return;
  }

  const x0 = from.x;
  const y0 = from.y;
  const x1 = to.x;
  const y1 = to.y;

  let col = Math.floor(x0 / tileSize);
  let row = Math.floor(y0 / tileSize);
  const endCol = Math.floor(x1 / tileSize);
  const endRow = Math.floor(y1 / tileSize);

  const visitIfInBounds = (c: number, r: number): void => {
    if (c < 0 || r < 0 || c >= widthTiles || r >= heightTiles) {
      return;
    }
    visit(tileIndexAt(c, r, widthTiles));
  };

  visitIfInBounds(col, row);
  if (col === endCol && row === endRow) {
    return;
  }

  const dx = x1 - x0;
  const dy = y1 - y0;
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
  const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;

  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  const tDeltaX = stepX !== 0 ? tileSize / absDx : Number.POSITIVE_INFINITY;
  const tDeltaY = stepY !== 0 ? tileSize / absDy : Number.POSITIVE_INFINITY;

  let tMaxX: number;
  if (stepX > 0) {
    tMaxX = ((col + 1) * tileSize - x0) / dx;
  }
  else if (stepX < 0) {
    tMaxX = (col * tileSize - x0) / dx;
  }
  else {
    tMaxX = Number.POSITIVE_INFINITY;
  }

  let tMaxY: number;
  if (stepY > 0) {
    tMaxY = ((row + 1) * tileSize - y0) / dy;
  }
  else if (stepY < 0) {
    tMaxY = (row * tileSize - y0) / dy;
  }
  else {
    tMaxY = Number.POSITIVE_INFINITY;
  }

  const maxSteps = Math.abs(endCol - col) + Math.abs(endRow - row) + 2;
  for (let step = 0; step < maxSteps; step++) {
    if (col === endCol && row === endRow) {
      break;
    }

    if (tMaxX < tMaxY) {
      tMaxX += tDeltaX;
      col += stepX;
    }
    else if (tMaxY < tMaxX) {
      tMaxY += tDeltaY;
      row += stepY;
    }
    else {
      tMaxX += tDeltaX;
      tMaxY += tDeltaY;
      col += stepX;
      row += stepY;
    }

    visitIfInBounds(col, row);
  }
}

/** Collects tile indices crossed by a world segment (see {@link forEachTileIndexAlongWorldSegment}). */
export function tileIndicesAlongWorldSegment(
  from: ICoord2D,
  to: ICoord2D,
  tileSize: number,
  widthTiles: number,
  heightTiles: number
): number[] {
  const tiles: number[] = [];
  forEachTileIndexAlongWorldSegment(from, to, tileSize, widthTiles, heightTiles, (tileIndex) => {
    tiles.push(tileIndex);
  });
  return tiles;
}
